import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import type { DashboardPanelKey } from "@dashai/hooks/useDashboardPanelChat";
import { aiColumnSelectionLabels } from "@/utils/aiColumnPresence";

// What the Questions panel needs from the dashboard page it sits beside: which
// dashboard it is, its current view, and the filters it shows (label → display
// value). dashaiChat decides from the view whether the panel can take a
// question there. Each page reports these, since
// most keep their filters in page state the shell cannot read. A page that
// reports nothing gets no panel. GeneralLayout provides the context, and also
// keeps the panel's open state, so the panel stays open across dashboards.
type DashboardFilters = Record<string, string>;

export interface DashboardQuestion {
  dashboard: DashboardPanelKey | null;
  filters: DashboardFilters;
  /** The page's current view, such as a Key Accounts tab, or null when it has one view. */
  view: string | null;
}

const NO_DASHBOARD: DashboardQuestion = { dashboard: null, filters: {}, view: null };

const ReportContext = createContext<DashboardQuestion>(NO_DASHBOARD);
const SetReportContext = createContext<((report: DashboardQuestion) => void) | null>(null);
const PanelOpenContext = createContext<[boolean, (open: boolean) => void]>([false, () => {}]);

export function DashboardQuestionProvider({ children }: { children: ReactNode }) {
  const [report, setReport] = useState<DashboardQuestion>(NO_DASHBOARD);
  const [open, setOpen] = useState(false);
  const panelOpen = useMemo<[boolean, (o: boolean) => void]>(() => [open, setOpen], [open]);
  return (
    <SetReportContext.Provider value={setReport}>
      <ReportContext.Provider value={report}>
        <PanelOpenContext.Provider value={panelOpen}>{children}</PanelOpenContext.Provider>
      </ReportContext.Provider>
    </SetReportContext.Provider>
  );
}

// Dashboard tables store a "not applicable" choice as this marker.
const NOT_APPLICABLE = "__null__";

function isRange(value: unknown): value is { from: number; to: number } {
  return typeof value === "object" && value !== null && "from" in value && "to" in value;
}

function displayItem(value: unknown): string {
  if (value === NOT_APPLICABLE) return "N/A";
  if (typeof value === "string") return aiColumnSelectionLabels([value])[0].trim();
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (isRange(value)) return `${value.from}–${value.to}`;
  return "";
}

function displayValue(value: unknown): string {
  if (!Array.isArray(value)) return displayItem(value);
  // A pair of numbers is a slider's [min, max].
  if (value.length === 2 && value.every((v) => typeof v === "number"))
    return `${value[0]}–${value[1]}`;
  return value.map(displayItem).filter(Boolean).join(", ");
}

function keepDisplayValues(filters: Record<string, unknown>): DashboardFilters {
  const kept: DashboardFilters = {};
  for (const [label, value] of Object.entries(filters)) {
    const shown = displayValue(value);
    if (shown) kept[label] = shown;
  }
  return kept;
}

interface DashboardQuestionReport {
  dashboard: DashboardPanelKey;
  filters: Record<string, unknown>;
  view?: string | null;
}

/**
 * Called by a dashboard page to put the Questions panel beside it. Each filter
 * becomes display text: text as is, yes/no as Yes or No, a range as from–to,
 * and a list as its items joined. An empty value is left out. The report
 * clears on unmount.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useReportDashboardQuestion({
  dashboard,
  filters,
  view = null,
}: DashboardQuestionReport): void {
  const setReport = useContext(SetReportContext);
  const serialized = JSON.stringify(keepDisplayValues(filters));

  useEffect(() => {
    setReport?.({ dashboard, filters: JSON.parse(serialized) as DashboardFilters, view });
  }, [setReport, dashboard, serialized, view]);

  useEffect(() => () => setReport?.(NO_DASHBOARD), [setReport]);
}

/** What the current dashboard page reported, or no dashboard. */
// eslint-disable-next-line react-refresh/only-export-components
export function useDashboardQuestion(): DashboardQuestion {
  return useContext(ReportContext);
}

/** Whether the Questions panel is open, kept by the shell across dashboards. */
// eslint-disable-next-line react-refresh/only-export-components
export function useQuestionsPanelOpen(): [boolean, (open: boolean) => void] {
  return useContext(PanelOpenContext);
}
